import { FC, useEffect, useState } from "react";
import { Box, Button, Fade, MenuItem, SelectChangeEvent, Slide } from "@mui/material";
import useTranslate from "../../../hooks/useTranslate";
import TimeInterval from "../../TimeInterval/TimeInterval";
import { ICallingLineIntervals } from "./CallingLineIntervals.props";
import styles from "./CallingLineIntervals.module.scss";
import {
	useAddTimeIntervalMutation,
	useDeleteTimeIntervalMutation,
	useGetCallingListsQuery,
	useGetTimeintervalsQuery,
	useUpdateCallingListTimeintervalsMutation,
} from "@/store/RTKQuery/clsSlice";
import { defaultTimeintervals } from "./CallingLineIntervals.const";
import { DaysType, ITimeInterval } from "@/types/clsTypes";
import { BlockWithError, CustomSelect } from "@/ui";
import { v4 as uuidv4 } from "uuid";
import NoticeSingleAction from "@/components/MUI/NoticeSingleAction/NoticeSingleAction";
import { useAccessRight } from "@/hooks";
import { CLS } from "@/constants/accessRights";
import { Hint } from "@/HOC";

const CallingLineIntervals: FC<ICallingLineIntervals> = ({ showIntervals, setShowIntervals, hint = "" }) => {
	const [showNotification, setShowNotification] = useState<boolean>(false); // показ уведомления
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [intervalName, setIntervalName] = useState<string>(""); // выбранное имя интервала
	const [changeFlg, setChangeFlg] = useState<boolean>(false); // флаг, уведомляющий об изменении данных и возможности сохранить эти изменения
	const [currentTimeinterval, setCurrentTimeinterval] = useState<ITimeInterval>(defaultTimeintervals);
	const { data, isLoading } = useGetCallingListsQuery(
		{},
		{
			refetchOnMountOrArgChange: false,
			refetchOnReconnect: false,
		}
	);
	const { data: timeintervalsList, refetch } = useGetTimeintervalsQuery(undefined, {
		refetchOnMountOrArgChange: false,
		refetchOnReconnect: false,
	});
	const [updateCallingListTimeintervals] = useUpdateCallingListTimeintervalsMutation({});
	const [addTimeInterval] = useAddTimeIntervalMutation();
	const [deleteTimeInterval] = useDeleteTimeIntervalMutation();

	const translate = useTranslate(); // hook для перевода текста
	const isAccess = useAccessRight();

	useEffect(() => {
		if (data && !isLoading && data.error !== 1) {
			if (timeintervalsList && timeintervalsList.timeintervals) {
				// Если текущий интервал уже выбран и он есть в новом списке — не сбрасываем
				const foundCurrent = timeintervalsList.timeintervals.find((el) => el.id === currentTimeinterval.id);
				if (foundCurrent) {
					setCurrentTimeinterval(foundCurrent);
					setIntervalName(foundCurrent.name);
					return;
				}

				// Иначе выбрать default
				const defaultTimeinterval = timeintervalsList.timeintervals.find((el) => el.id === "default");
				if (defaultTimeinterval) {
					setCurrentTimeinterval(defaultTimeinterval);
					setIntervalName(defaultTimeinterval.name);
				}
			}
		}
	}, [timeintervalsList, data]);

	// обработчик закрытия вкладки
	const closeHandler = () => {
		setShowIntervals(false);
	};
	const handleUpdateCallingList = (newTimeintervalId: string) => {
		if (!data || !data.lists) return;
		const changedTimeInterval = timeintervalsList?.timeintervals.find((el) => el.id === newTimeintervalId);

		if (!changedTimeInterval) return;

		setCurrentTimeinterval(changedTimeInterval);
		setIntervalName(changedTimeInterval.name);
	};

	const handleSaveChanges = () => {
		if (data && currentTimeinterval) {
			updateCallingListTimeintervals({ intervalId: currentTimeinterval.id, newTimeintervals: currentTimeinterval }).then(() => {
				setShowNotification(true);
				setSnackbarMessage("notice_timeIntervalUpdated");
				setChangeFlg(false);
			});
		}
	};
	//показать уведомление с сообщением
	const handleShowNotice = (message: string) => {
		setSnackbarMessage(message);
		setShowNotification(true);
	};

	// показать уведомление исходя из ответа
	const handleMutationResponse = (res: { error?: number }, successMsg: string, errorMsg: string, onSuccessFn?: (() => void) | (() => void)[]) => {
		handleShowNotice(res.error === 0 ? successMsg : errorMsg);

		if (res.error === 0) {
			if (Array.isArray(onSuccessFn)) {
				onSuccessFn.forEach((fn) => fn());
			} else {
				onSuccessFn?.();
			}
		}
	};

	// проверка прав на изменения
	const checkAccesToChangeTimeInterval = (changeFn: () => void) => {
		if (isAccess(CLS.TIMEINTERVALS_MODIFY)) {
			changeFn();
			setChangeFlg(true);
		}
	};

	const updateCurrentInterval = (updater: (prev: ITimeInterval) => ITimeInterval) => {
		checkAccesToChangeTimeInterval(() => setCurrentTimeinterval(updater));
	};

	const toggleNumberInArray = (arr: number[], value: number): number[] => (arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]);

	// обработчик изменения дней по производственному календарю
	const handleChangeDays = (e: SelectChangeEvent<DaysType>) => updateCurrentInterval((prev) => ({ ...prev, days: e.target.value as DaysType }));

	// обработчик изменения дней месяца
	const handleChangeDate = (dates: number[], date: number) => updateCurrentInterval((prev) => ({ ...prev, dates: toggleNumberInArray(dates, date) }));

	// обработчик изменения дней недели
	const handleChangeWeekDays = (weekdays: number[], day: number) => updateCurrentInterval((prev) => ({ ...prev, weekdays: toggleNumberInArray(weekdays, day) }));

	// обработчик изменения месяцев
	const handleChangeMonths = (months: number[], month: number) => updateCurrentInterval((prev) => ({ ...prev, months: toggleNumberInArray(months, month) }));

	// обработчик изменения названия
	const handleChangeName = (name: string) => {
		checkAccesToChangeTimeInterval(() => {
			if (intervalName !== name) {
				setCurrentTimeinterval((prev) => ({ ...prev, name }));
				setIntervalName(name);
			}
		});
	};

	// обработчик добавления времени интервала
	const handleAddTimePeriod = () =>
		updateCurrentInterval((prev) => ({
			...prev,
			intervals: [...prev.intervals, [0, 1440]],
		}));

	const handleAddTimeInterval = () => {
		checkAccesToChangeTimeInterval(() => {
			let newId = uuidv4();

			if (timeintervalsList?.timeintervals.some((el) => el.id === newId)) {
				newId = uuidv4(); // генерируем новый ID, если такой уже существует
			}
			addTimeInterval({
				timeintervals: {
					id: uuidv4(),
					name: `Новый интервал ${timeintervalsList?.timeintervals.length || 0 + 1}`,
					days: "all",
					weekdays: defaultTimeintervals.weekdays,
					months: defaultTimeintervals.months,
					dates: defaultTimeintervals.dates,
					intervals: [[0, 1440]], // добавляем один интервал с 0 до 1440 минут
				},
			})
				.unwrap()
				.then((res) =>
					handleMutationResponse(res, "notice_timeIntervalAdded", `${translate("notice_timeIntervalAddError")}: ${res.message}`, () => {
						setTimeout(() => {
							refetch()
								.unwrap()
								.then((updated) => {
									const foundInterval = updated.timeintervals.find((interval) => interval.id === res.id);

									if (foundInterval) {
										setCurrentTimeinterval(foundInterval);
										setIntervalName(foundInterval.name);
									}
								});
						}, 100);
					})
				)
				.catch((err) => handleShowNotice(`${translate("notice_deletionError")}: ${JSON.stringify(err.error)}`));
		});
	};

	const handleChangeTimeInterval = (timeInterval: [number, number], idx?: number) => {
		if (idx !== undefined) {
			setCurrentTimeinterval((prev) => ({
				...prev,
				intervals: prev.intervals.map((interval, i) => (i === idx ? timeInterval : interval)),
			}));
		}
	};

	const handleDeleteTimeInterval = () => {
		if (timeintervalsList) {
			const defaultInterval = timeintervalsList.timeintervals.find((el) => el.id === "default") || defaultTimeintervals;

			if (currentTimeinterval) {
				deleteTimeInterval({ timeIntervalId: currentTimeinterval.id })
					.unwrap()
					.then((res) =>
						handleMutationResponse(res, "notice_timeIntervalDeleted", `${translate("notice_deletionError")}: ${res.message}`, () =>
							setCurrentTimeinterval(defaultInterval)
						)
					)
					.catch((err) => handleShowNotice(`${translate("notice_deletionError")}: ${JSON.stringify(err.error)}`));
			}
		}
	};

	const handleDeleteTime = (idx: number) =>
		updateCurrentInterval((prev) => ({
			...prev,
			intervals: prev.intervals.filter((_, i) => i !== idx),
		}));

	return (
		<>
			<Slide direction="left" in={showIntervals} mountOnEnter unmountOnExit>
				<div className={styles.modal} onMouseDown={closeHandler}>
					<div className={styles.config} onMouseDown={(e) => e.stopPropagation()}>
						{/* настройки интервалов */}
						{timeintervalsList?.error === 1 ? (
							<BlockWithError updateHandler={refetch} />
						) : (
							<Fade in={true} timeout={500}>
								<Hint hint={hint} position="left" offset={[0, -20]}>
									<div className={styles.configWrapper}>
										<div className={styles.configTop}>
											{/* временной интервал */}
											<Box sx={{ mt: "8px" }}>
												<CustomSelect
													variantsList={timeintervalsList?.timeintervals.map((timeinterval) => ({ value: timeinterval.id, title: timeinterval.name })) ?? []}
													valueIndex={timeintervalsList?.timeintervals.findIndex((el) => el.id === currentTimeinterval.id) ?? 0}
													setState={handleUpdateCallingList}
													elementWidth={"full"}
													title={translate("select_timeInterval")}
													height={33}
												>
													{isAccess(CLS.TIMEINTERVALS_ADD) ? (
														<MenuItem sx={{ fontSize: 13 }} onClick={handleAddTimeInterval}>
															{translate("selectItem_addTimeInterval")}...
														</MenuItem>
													) : (
														<></>
													)}
												</CustomSelect>
											</Box>
											<TimeInterval
												timeinterval={currentTimeinterval}
												intervalName={intervalName}
												setIntervalName={setIntervalName}
												setChangeFlg={setChangeFlg}
												changeDaysHandler={handleChangeDays}
												changeDateHandler={handleChangeDate}
												changeWeekDaysHandler={handleChangeWeekDays}
												changeMonthsHandler={handleChangeMonths}
												changeNameHandler={handleChangeName}
												addTimePeriodHandler={handleAddTimePeriod}
												changeTimeIntervalHandler={handleChangeTimeInterval}
												deleteTimeHandler={handleDeleteTime}
											/>
										</div>

										<div className={styles.configBottom}>
											{isAccess(CLS.TIMEINTERVALS_MODIFY) && (
												<Button variant="outlined" disabled={!changeFlg} sx={{ fontSize: 11, width: "100%" }} onClick={handleSaveChanges}>
													{translate("button_save")}
												</Button>
											)}
											{isAccess(CLS.TIMEINTERVALS_DELETE) && (
												<Button
													variant="outlined"
													disabled={currentTimeinterval.id === "default" || !currentTimeinterval}
													sx={{ fontSize: 11, width: "100%" }}
													color={"error"}
													onClick={handleDeleteTimeInterval}
												>
													{translate("button_delete")}
												</Button>
											)}
										</div>
									</div>
								</Hint>
							</Fade>
						)}

						<div className={styles.tagClose} onClick={closeHandler}>
							{translate("button_close")}
						</div>
					</div>
				</div>
			</Slide>

			<NoticeSingleAction
				severity={snackbarMessage.toLowerCase().includes("error") || snackbarMessage.toLowerCase().includes("ошибка") ? "error" : "info"}
				showNotification={showNotification}
				setShowNotification={setShowNotification}
				title={snackbarMessage}
			/>
		</>
	);
};

export default CallingLineIntervals;
